import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Divider, Select } from '@mantine/core';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import ViewProductPrintImagesCanvasGrid from './ViewProductPrintImagesCanvasGrid';
import ViewProductPrintImagesCanvasControls from './ViewProductPrintImagesCanvasControls';
import { ECOM_IMAGE_POSITION_LIST } from '../../../config/constants';
import AppFlexbox from '../../common/AppFlexbox';
import { groupVendorPrintDesignsByVariant } from '../../../helpers/vendorHelper';

const ViewProductPrintImagesModal = ({
  ecomVendorProduct,
  isOpen,
  onClose
}) => {
  const containerRef = useRef(null);
  const [viewState, setViewState] = useState({
    selectedVariantDesignId: null,
    selectedPrintDesignId: null,
    selectedPrintImageValue: null,
    showGridlines: false,
    showOriginalCanvasSize: false,
    showBackgroundFill: false
  });

  const printDesigns =
    ecomVendorProduct?.ecomVendorProductPrintDesigns?.map((d) => ({
      pkEcomVendorProductPrintDesign: d.pkEcomVendorProductPrintDesign.toString(),
      fkEcomImagePosition: d.fkEcomImagePosition.toString(),
      variantIds: d.ecomVendorProductPrintDesignVariants.map((v) => v.uuid),
      label:
        ECOM_IMAGE_POSITION_LIST.find(
          (p) => p.value === d.fkEcomImagePosition.toString()
        )?.label || '',
      images: d.ecomVendorProductPrintDesignImages
        .map((i) => ({
          isPreview: i.sizeOption === 'preview',
          imageWidthPx: i.imageWidthPx,
          imageHeightPx: i.imageHeightPx,
          sizeOption: i.sizeOption,
          label: `${i.sizeOption} (${i.imageWidthPx}x${i.imageHeightPx}px)`,
          value: i.sizeOption,
          src: i.src
        }))
        .sort((a, b) =>
          !a.isPreview && b.isPreview
            ? -1
            : b.imageWidthPx * b.imageHeightPx -
              a.imageWidthPx * a.imageHeightPx
        )
    })) || [];

  const variantPrintDesigns = groupVendorPrintDesignsByVariant(printDesigns);
  const selectedVariantDesign = variantPrintDesigns.find(
    (d) => d.id === viewState.selectedVariantDesignId
  );
  const selectedPrintDesign = printDesigns.find(
    (d) => d.pkEcomVendorProductPrintDesign === viewState.selectedPrintDesignId
  );

  const selectedPrintImage =
    selectedPrintDesign?.images.find(
      (i) => i.sizeOption === viewState.selectedPrintImageValue
    ) || null;

  useEffect(() => {
    if (isOpen) {
      const defaultVariantDesign =
        variantPrintDesigns.find((d) => d.isMainDesign) ||
        variantPrintDesigns[0];

      setViewState((prevState) => ({
        ...prevState,
        selectedVariantDesignId: defaultVariantDesign?.id || null,
        selectedPrintDesignId:
          defaultVariantDesign?.designs[0]?.pkEcomVendorProductPrintDesign ||
          null,
        selectedPrintImageValue:
          defaultVariantDesign?.designs[0]?.images[0]?.value || null,
        showGridlines: false,
        showOriginalCanvasSize: false,
        showBackgroundFill: false
      }));
    }
  }, [isOpen]);

  const onDownloadImage = (image) => {
    fetch(image.src)
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        // Set filename
        a.download = image.src.split('/').pop();
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => console.error('Failed to download', image.src, err));
  };

  const downloadImages = async () => {
    printDesigns.forEach((design) => {
      design.images.forEach(onDownloadImage);
    });
  };

  return (
    ecomVendorProduct && (
      <ResponsiveModal
        formSectionProps={{
          onClose,
          cancelTitle: 'Close',
          cancelVariant: 'filled',
          isSubmitHidden: true,
          leftSection: (
            <Anchor
              onClick={downloadImages}
              style={{ fontSize: 14, color: 'dodgerblue' }}
            >
              Download all images
            </Anchor>
          )
        }}
        isOpen={isOpen}
        onClose={onClose}
        size={700}
        title="Product print images"
      >
        <AppStack style={{ gap: 20 }}>
          <AppText
            style={{
              fontSize: 14,
              fontStyle: 'italic',
              color: '#666',
              textAlign: 'center'
            }}
          >
            These images will be submitted to the print provider when orders are
            placed for this product.
          </AppText>

          <Divider />

          <AppStack style={{ gap: 5 }}>
            {printDesigns.length > 1 && (
              <AppFlexbox
                style={{
                  gap: 10,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                  <Select
                    data={
                      selectedVariantDesign?.designs.map((d) => ({
                        value: d.pkEcomVendorProductPrintDesign,
                        label: d.label
                      })) || []
                    }
                    onChange={(value) =>
                      setViewState((prev) => ({
                        ...prev,
                        selectedPrintDesignId: value,
                        selectedPrintImageValue:
                          selectedVariantDesign?.designs.find(
                            (d) => d.pkEcomVendorProductPrintDesign === value
                          )?.images[0]?.value || null
                      }))
                    }
                    placeholder="Position"
                    value={viewState.selectedPrintDesignId}
                  />
                </AppFlexbox>

                {variantPrintDesigns.length > 1 && (
                  <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                    <Select
                      data={variantPrintDesigns.map((d) => ({
                        value: d.id,
                        label: d.label
                      }))}
                      onChange={(value) => {
                        const nextVariantDesign = variantPrintDesigns.find(
                          (d) => d.id === value
                        );

                        const selectedPrintDesignForNextVariant =
                          nextVariantDesign?.designs.find(
                            (d) =>
                              d.fkEcomImagePosition ===
                              selectedPrintDesign?.fkEcomImagePosition
                          ) || nextVariantDesign?.designs[0];

                        const selectedPrintImageForNextVariant =
                          selectedPrintDesignForNextVariant?.images.find(
                            (i) =>
                              i.sizeOption === viewState.selectedPrintImageValue
                          ) || selectedPrintDesignForNextVariant?.images[0];

                        setViewState((prev) => ({
                          ...prev,
                          selectedVariantDesignId:
                            nextVariantDesign?.id || null,
                          selectedPrintDesignId:
                            selectedPrintDesignForNextVariant?.pkEcomVendorProductPrintDesign ||
                            null,
                          selectedPrintImageValue:
                            selectedPrintImageForNextVariant?.value || null
                        }));
                      }}
                      placeholder="Variant Design"
                      value={viewState.selectedVariantDesignId}
                    />
                  </AppFlexbox>
                )}
              </AppFlexbox>
            )}

            <ViewProductPrintImagesCanvasControls
              formState={viewState}
              onDownloadImage={
                selectedPrintImage
                  ? () => {
                      if (selectedPrintImage) {
                        onDownloadImage(selectedPrintImage);
                      }
                    }
                  : null
              }
              options={selectedPrintDesign?.images || []}
              setFormState={setViewState}
            />
            <AppStack
              ref={containerRef}
              style={{
                gap: 5,
                border: 'solid 1px #363434ff'
              }}
            >
              <ViewProductPrintImagesCanvasGrid
                originalCanvasHeight={selectedPrintImage?.imageHeightPx}
                originalCanvasWidth={selectedPrintImage?.imageWidthPx}
                showBackgroundFill={viewState.showBackgroundFill}
                showGrid={viewState.showGridlines}
                showOriginalCanvasSize={viewState.showOriginalCanvasSize}
                src={selectedPrintImage?.src}
              />
            </AppStack>
          </AppStack>
        </AppStack>
      </ResponsiveModal>
    )
  );
};

ViewProductPrintImagesModal.propTypes = {
  ecomVendorProduct: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

export default ViewProductPrintImagesModal;
